/**
 * -----------------------------------------------------------------------------
 * User Detail Page
 * -----------------------------------------------------------------------------
 * This page is used to show the details of a single user of the organization.
 */

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import { RESPONSE_CODE } from "../../../api/network/constants";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import {
  useEditEnterpriseMutation,
  useListEnterprisesQuery,
  useSingleEnterpriseQuery,
  useGetAvailableChildAccountsQuery,
} from "../../../api/network/sysadminApiServices";
import {
  TListingQueryParams,
  TModalsState,
  setListingQueryParams,
  setModalsData,
} from "../../../api/store/commonSlice";
import { EditOrganizationAccountPayload } from "../../../api/types/Admin";
import { TEnterprise } from "../../../api/types/Sysadmin";
import svgIcons from "../../../assets/svg/svgIcons";
import Accordian from "../../../components/accordian";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import ModalDeleteConfirmation from "../../../components/admin/modalDeleteConfirmation";
import AdminListingColumnItem from "../../../components/adminListingColumnItem";
import FillAllRequiredFieldsCaption from "../../../components/fillAllRequiredFieldsCaption";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import AppSearchBox from "../../../components/searchBox";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { routeUrls } from "../../../navigation/routeUrls";
import { enterpriseDisplayName } from "../../../utils/enterprise";
import { useLoggedInUserData } from "../../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../role/constants";
import { EnterpriseGeneralDetailsForm } from "./enterprise-form";
import {
  TFormFieldNames,
  enterpriseDetailsInitialValues,
  enterpriseDetailsYupValidationSchema,
} from "./validation";
import {
  DISTANCE_UNITS_OPTIONS,
  ECONOMY_UNITS_OPTIONS,
  LATITUDE_LONGITUDE_FORMAT_OPTIONS,
  PRESSURE_UNITS_OPTIONS,
  ROUTE_LINE_THICKNESS_OPTIONS,
  SPEED_UNITS_OPTIONS,
  TEMPRATURE_UNITS_OPTIONS,
  VOLUME_UNITS_OPTIONS,
} from "../account/constants";
import FormActionButtons, {
  FormActionButton,
} from "../../../components/admin/formActionButtons";

const ScreenSysadminDetailEnterprise = () => {
  const { enterpriseId } = useParams<{ enterpriseId: string }>();
  const { state: locationState } = useLocation(); // OrganizationEntityListingPayload | { new: true }
  const { t: tMain } = useTranslation();
  const { t: tAdmin } = useTranslation("translation", {
    keyPrefix: "sysadmin.enterprises",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "sysadmin.enterprises.detailsPage",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { getUserACLAccessLevel } = useACL();

  const thisUserTimezone = useLoggedInUserData("timezone");

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { sysadminEnterprises: sysadminEnterprisesQueryParams } =
    listingQueryParams;

  // flag to idenfiy if user is coming from create new user popup
  const isNewEntity = React.useRef<boolean>(!!locationState?.new);

  // flag to enable edit mode
  const [userCanEdit, setUserCanEdit] = useState<boolean>(
    !!isNewEntity?.current
  );

  // prepare query params for fetching organization users
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        sysadminEnterprises: {
          ...sysadminEnterprisesQueryParams,
          page: 1,
          search: value,
        },
      })
    );
  }, 500);

  // fetch child accounts
  const {
    data: availableChildrenData,
    isFetching: isFetchingAvailableChildren,
  } = useGetAvailableChildAccountsQuery(enterpriseId || "", {
    skip: !enterpriseId,
  });

  // fetch organization users
  const {
    data: dataEnterprises,
    isFetching: isFetchingEnterprises,
    error,
  } = useListEnterprisesQuery(sysadminEnterprisesQueryParams);

  // fetch single enterprise details
  const {
    data: dataSingleEnterprise,
    isFetching: isFetchingSingleEnterprise,
    isError: isErrorSingleEnterprise,
    error: errorSingleEnterprise,
  } = useSingleEnterpriseQuery(
    {
      enterprise_id: `${enterpriseId}`,
    },
    {
      skip: !enterpriseId,
      refetchOnMountOrArgChange: true, // prevent cached values from displaying when creating entity with same id
    }
  );
  useEffect(() => {
    if (isErrorSingleEnterprise) {
      if (
        (errorSingleEnterprise as FetchBaseQueryError)?.status ===
          RESPONSE_CODE.NOT_FOUND ||
        (errorSingleEnterprise as FetchBaseQueryError)?.status ===
          RESPONSE_CODE.BAD_REQUEST
      ) {
        toast.error(t("toast.enterprise_not_found"));
        navigate(routeUrls.sysadminChildren.enterprises);
      }
    }
  }, [isErrorSingleEnterprise, errorSingleEnterprise]);

  // enterprise mutation
  const [editEnterpriseApiTrigger, { isLoading: isLoadingEditAccount }] =
    useEditEnterpriseMutation();
  // const [deleteSingleUserApiTrigger, { isLoading: isLoadingDeleteUser }] = useDeleteSingleUserMutation();
  const { results, count } = dataEnterprises || {};
  const currentEnterpriseList = results?.find(
    (item: TEnterprise) => item.accountid === enterpriseId
  );

  // formik
  const [formikValuesReady, setFormikValuesReady] = useState<boolean>(false);
  useEffect(() => {
    setFormikValuesReady(!isFetchingSingleEnterprise);
  }, [isFetchingSingleEnterprise]);

  const eligibleChildOptions = useMemo(() => {
    const dataArray = Array.isArray(availableChildrenData)
      ? availableChildrenData
      : availableChildrenData?.results || availableChildrenData?.data || [];

    if (dataArray.length === 0) return [];

    return dataArray.map((acc: any) => ({
      label: acc.description
        ? `${acc.accountid} | ${acc.description}`
        : acc.accountid,
      value: acc.accountid,
    }));
  }, [availableChildrenData]);

  const [showParentWarning, setShowParentWarning] = useState<boolean>(false);
  const handleParentToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;

    if (isChecked && currentEnterpriseList?.is_child_account) {
      setShowParentWarning(true);
    } else {
      formik.setFieldValue("is_parent", isChecked);
      if (!isChecked) {
        formik.setFieldValue("child_accounts", []);
      }
    }
  };

  const onConfirmParentWarning = () => {
    formik.setFieldValue("is_parent", true);
    formik.setFieldValue("parent_id", "");
    setShowParentWarning(false);
  };

  const onCancelParentWarning = () => {
    setShowParentWarning(false);
  };
  const formik = useFormik({
    initialValues: enterpriseDetailsInitialValues,
    validationSchema: enterpriseDetailsYupValidationSchema,
    onSubmit: (values) => {
      const preparedEnterpriseEditParam = {
        account_id: enterpriseId,
        data: {
          description: values.account_description,
          ...(!!values.password ? { password: values.password } : {}),
          timezone: values.timezone,
          isactive: values.is_active ? 1 : 0,
          toggleft: values.toggleft ? 1 : 0,
          contactname: values.contact_name,
          contactphone: values.contact_phone,
          contactemail: values.contact_email,
          suspenduntiltime: 0, // unlock_account: false, // NOT USED IN FT 1.0, DEFAULTING TO 0
          hasdriversassigned: 0, // values.drivers_assigned_to_devices ? 1 : 0, // NOT USED IN FT 1.0, DEFAULTING TO 0
          tomcattimeout: Math.floor((values.session_timeout || 0) / 60), // convert seconds to minutes (for BE)
          // autoadddevices:values.allow_auto_add_devices ? 1 : 0, // NOT NEEDED
          // maximumdevices: parseInt(values.maximum_devices),// NOT NEEDED
          // maxpingcount:values.max_commands_locates, // NOT NEEDED
          // totalpingcount: values.total, // NOT NEEDED
          isaccountmanager: values.is_parent ? 1 : 0,
          accountmanager: values.account_manager,
          is_parent: values.is_parent ? 1 : 0,
          child_account_ids:
            values.child_accounts?.map((acc: any) => acc.value) || [],
          parentaccountid: values.parent_id,
          privatelabelname: values.private_label_name,
          // geocodermode: values.geocoder_mode,// NOT NEEDED
          isbordercrossing: values.border_crossing ? 1 : 0,
          // dcspropertiesid: values.dcs_properties_id, // NOT NEEDED
          // smsproperties: values.sms_properties, // NOT NEEDED
          // allowwebservice: values.web_services ? 1 : 0,// NOT NEEDED
          hasfleettags: values.fleet_tag ? 1 : 0,
          hasfuelcardintegration: values.fuel_card_integration ? 1 : 0,
          haspanicsos: values.panic_sos ? 1 : 0,
          hassnowplows: values.snow_plows ? 1 : 0,
          enablecrashtrack: values.enablecrashtrack ? 1 : 0,
          usegeocategories: values.usegeocategories ? 1 : 0,
          elogenabled: values.driver_elogs ? 1 : 0,
          // elogaccountid: values.hos_data_on_maps ? 1 : 0, // NOT NEEDED // hos_data_on_maps: false, // TODO: NEED CONFIRMATION
          attributemask: 0, // "27_hour_reboot_option": false, // NOT USED IN FT 1.0, DEFAULTING TO 0
          // showspeedingbehavior: values.speed_behaviour_option ? 1 : 0, // NOT NEEDED
          showdtcpopups: values.dtc_popups_option ? 1 : 0,
          allowmaintlabels: values.maint_labels ? 1 : 0,
          allowptolabels: values.pto_labels ? 1 : 0,
          viewvideoallowed: values.view_video_option ? 1 : 0,
          // usesso: values.sso_to_vlstracks ? 1 : 0, // NOT NEEDED
          // usehamburgermenu: 0,// NOT NEEDED  // left_menu_hamburger: true, // NOT NEEDED IN FT 2.0, DEFAULTING TO 0
          hasdrivers: values.driver_groups ? 1 : 0,
          // defaultsnappedroute: values.snapped_route ? 1 : 0, // NOT NEEDED
          recordertype: values.recorder_type || 0,
          recorderconfigallowed: values.recorder_config_allowed || 0,
          speedunits: values.speed_units,
          distanceunits: `${values.distance_units || 0}`,
          volumeunits: `${values.volume_units || 0}`,
          economyunits: `${values.economy_units || 0}`,
          pressureunits: `${values.pressure_units || 0}`,
          temperatureunits: `${values.temperature_units || 0}`,
          latlonformat: `${values.latitude_longitude_format || 0}`,
          // routesegcolorsel: `${values.route_segment_color_rule || 0}`,// NOT NEEDED
          routestrokethickness: `${values.route_line_thickness || 0}`,
          // multivehiclemapname: `${values.multi_vehicle_map_name || 0}`, // NOT NEEDED
          // defaultuser: values.default_login_userId, // NOT NEEDED
          defaultoverlay: values.default_overlay,
          cameraliveviewduration: values.cameraliveviewduration || 30,
          minidleseconds: values.minidleseconds * 60 || 0, // convert minutes to seconds
          assetimageonmap: values.assetimageonmap ? 1 : 0,
          incidentreviewallowed: values.incidentreviewallowed ? 1 : 0,
          incabcoachingallowed: values.incabcoachingallowed ? 1: 0,
          serializedziptieallowed: values.serializedziptieallowed ? 1 : 0,
          userloginasallowed: values.userloginasallowed ? 1 : 0,
        },
      } as EditOrganizationAccountPayload;

      // // call api for updating enterprise
      editEnterpriseApiTrigger(preparedEnterpriseEditParam)
        .unwrap()
        .then(() => {
          toast.success(t("toast.enterprise_updated"));
          navigate(routeUrls.sysadminChildren.enterprises);
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.updation_failed")];
          toast.error(errors?.join(" "));
        });
    },
  });

  // pre-fill formik values
  useEffect(() => {
    if (dataSingleEnterprise && availableChildrenData) {
      console.log("dataSingleEnterprise", dataSingleEnterprise);
      console.log("child_account_ids", dataSingleEnterprise?.child_account_ids);
      setFormikValuesReady(false); // simulate render delay for select pre-selected values
      formik.setValues({
        account_id: dataSingleEnterprise?.accountid,
        account_description: dataSingleEnterprise?.description,
        creation_date: dataSingleEnterprise?.creationtime || "-",
        last_login: dataSingleEnterprise?.lastlogintime || "-",
        password: dataSingleEnterprise?.password || "",
        timezone: isNewEntity?.current
          ? (thisUserTimezone ?? "")
          : dataSingleEnterprise?.timezone || "",
        is_active: dataSingleEnterprise?.isactive === 1,
        toggleft: parseInt(`${dataSingleEnterprise?.toggleft}`) === 1,
        contact_name: dataSingleEnterprise?.contactname || "",
        contact_phone: dataSingleEnterprise?.contactphone || "",
        contact_email: dataSingleEnterprise?.contactemail || "",
        unlock_account: dataSingleEnterprise?.suspenduntiltime === 0, // NOT USED IN FT 1.0, DEFAULTING TO 0
        drivers_assigned_to_devices:
          dataSingleEnterprise?.hasdriversassigned === 1, // NOT USED IN FT 1.0, DEFAULTING TO 0
        session_timeout: (dataSingleEnterprise?.tomcattimeout || 0) * 60, // convert (BE) minutes to seconds
        allow_auto_add_devices: dataSingleEnterprise?.autoadddevices === 1,
        maximum_devices: `${dataSingleEnterprise?.maximumdevices || 0}`,
        max_commands_locates: dataSingleEnterprise?.maxpingcount || "0",
        total: dataSingleEnterprise?.totalpingcount || "0",
        account_manager: dataSingleEnterprise?.accountmanager || "",
        is_parent: dataSingleEnterprise?.isaccountmanager === 1,
        child_accounts:
          availableChildrenData
            ?.filter((acc: any) => acc.is_selected_child)
            .map((acc: any) => ({
              label: acc.description
                ? `${acc.accountid} | ${acc.description}`
                : acc.accountid,
              value: acc.accountid,
            })) || [],
        parent_id: dataSingleEnterprise?.parentaccountid || "",
        private_label_name: dataSingleEnterprise?.privatelabelname || "",
        geocoder_mode: dataSingleEnterprise?.geocodermode || "",
        border_crossing: dataSingleEnterprise?.isbordercrossing === 1,
        dcs_properties_id: dataSingleEnterprise?.dcspropertiesid || "",
        sms_properties: dataSingleEnterprise?.smsproperties || "",
        web_services: dataSingleEnterprise?.allowwebservice === 1,
        fleet_tag: dataSingleEnterprise?.hasfleettags === 1,
        panic_sos: dataSingleEnterprise?.haspanicsos === 1,
        fuel_card_integration:
          dataSingleEnterprise?.hasfuelcardintegration === 1,
        snow_plows: dataSingleEnterprise?.hassnowplows === 1,
        enablecrashtrack: dataSingleEnterprise.enablecrashtrack === 1,
        usegeocategories: dataSingleEnterprise.usegeocategories === 1,
        driver_elogs: dataSingleEnterprise?.elogenabled === 1,
        hos_data_on_maps: dataSingleEnterprise?.elogaccountid === 1,
        "27_hour_reboot_option": dataSingleEnterprise?.attributemask === 1, // NOT USED IN FT 1.0, DEFAULTING TO 0
        speed_behaviour_option:
          dataSingleEnterprise?.showspeedingbehavior === 1,
        dtc_popups_option: dataSingleEnterprise?.showdtcpopups === 1,
        maint_labels: dataSingleEnterprise?.allowmaintlabels === 1,
        pto_labels: dataSingleEnterprise?.allowptolabels === 1,
        view_video_option: dataSingleEnterprise?.viewvideoallowed === 1,
        sso_to_vlstracks: dataSingleEnterprise?.usesso === 1,
        left_menu_hamburger: dataSingleEnterprise?.usehamburgermenu === 1,
        assetimageonmap: dataSingleEnterprise?.assetimageonmap == 1,
        serializedziptieallowed: dataSingleEnterprise?.serializedziptieallowed === 1,
        userloginasallowed: dataSingleEnterprise?.userloginasallowed === 1,
        incidentreviewallowed: dataSingleEnterprise?.incidentreviewallowed === 1,
        incabcoachingallowed: dataSingleEnterprise?.incabcoachingallowed === 1,
        driver_groups: dataSingleEnterprise?.hasdrivers === 1,
        snapped_route: dataSingleEnterprise?.defaultsnappedroute === 1,
        recorder_type: dataSingleEnterprise?.recordertype?.toString() ?? "",
        recorder_config_allowed:
          dataSingleEnterprise?.recorderconfigallowed?.toString() ?? "0",
        speed_units: `${dataSingleEnterprise.speedunits ?? SPEED_UNITS_OPTIONS[0].value}`,
        distance_units: `${dataSingleEnterprise.distanceunits ?? DISTANCE_UNITS_OPTIONS[0].value}`,
        volume_units: `${dataSingleEnterprise.volumeunits ?? VOLUME_UNITS_OPTIONS[0].value}`,
        economy_units: `${dataSingleEnterprise.economyunits ?? ECONOMY_UNITS_OPTIONS[0].value}`,
        pressure_units: `${dataSingleEnterprise.pressureunits ?? PRESSURE_UNITS_OPTIONS[0].value}`,
        temperature_units: `${dataSingleEnterprise.temperatureunits ?? TEMPRATURE_UNITS_OPTIONS[0].value}`,
        latitude_longitude_format: `${dataSingleEnterprise.latlonformat ?? LATITUDE_LONGITUDE_FORMAT_OPTIONS[0].value}`,
        cameraliveviewduration: dataSingleEnterprise.cameraliveviewduration ?? 30,
        minidleseconds: dataSingleEnterprise.minidleseconds ? Math.floor(Number(dataSingleEnterprise.minidleseconds) / 60) : 0, // convert seconds to minutes
        route_segment_color_rule: dataSingleEnterprise.routesegcolorsel ?? "",
        route_line_thickness: `${dataSingleEnterprise.routestrokethickness ?? ROUTE_LINE_THICKNESS_OPTIONS[1].value}`,
        multi_vehicle_map_name: dataSingleEnterprise.multivehiclemapname ?? "",
        default_login_userId: dataSingleEnterprise.defaultuser ?? "",
        default_overlay: `${dataSingleEnterprise.defaultoverlay ?? ""}`,
        // -------------------------------
        // user_id: dataSingleEnterprise?.userid,
        // password: "",
        // user_description: dataSingleEnterprise?.description || "",
        // is_active: dataSingleEnterprise?.isactive === 1,
        // use_two_factor: dataSingleEnterprise?.requiretwofactor === 1,
        // use_geozone_labels: dataSingleEnterprise?.enablegeozonelabels === 1,
        // contact_name: dataSingleEnterprise?.contactname || "",
        // contact_phone_number: dataSingleEnterprise?.contactphone || "",
        // contact_email: dataSingleEnterprise?.contactemail || "",
        // timezone: isNewEntity?.current ? thisUserTimezone ?? "" : dataSingleEnterprise?.timezone || "",
        // enable_sso_to_visatracks: dataSingleEnterprise?.usesso === 1,
        // default_overlay: dataSingleEnterprise?.defaultoverlay || "",
        // user_state: "", // TODO: fix user state
        // session_timeout: dataSingleEnterprise?.tomcattimeout || 0,
        // first_login_page: isNewEntity?.current ? FIRST_LOGIN_PAGE_OPTIONS[0].value : dataSingleEnterprise?.firstloginpageid || "",
        // selected_groups:
        //   !!dataSingleEnterprise?.groups
        //     ? dataSingleEnterprise?.groups?.map((item) => item.groupid)
        //     : [],
        // all_groups: !dataSingleEnterprise?.groups || dataSingleEnterprise?.groups?.length === 0,
        // default_acl_role: dataSingleEnterprise?.roleid,
        // maximum_access_level: dataSingleEnterprise?.maxaccesslevel || 0,
      });
      setUserCanEdit(!!isNewEntity?.current);
      setTimeout(() => {
        setFormikValuesReady(true);
        formik.validateForm();
      }, 500); // simulate render delay for select pre-selected values
    }
  }, [dataSingleEnterprise, enterpriseId, availableChildrenData]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    dirty,
  } = formik;
  // console.log("VAL", values);
  // console.log("ERR", errors);

  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNames]
  );
  const isFormValid = invalidFields.length === 0 && dirty;

  // handle edit user
  const handleEditUser = () => {
    if (userCanEdit) {
      formik.handleSubmit();
    }
  };

  // handle delete user
  const handleDeleteEnterprise = () => {
    // TODO: NEED TO IMPLEMENT DELETE ENTERPRISE
    // deleteSingleUserApiTrigger({ email: dataSingleEnterprise?.contactemail ?? '' })
    //   .unwrap()
    //   .then(() => {
    //     toast.success(t("toast.enterprise_deleted"));
    //     navigate(routeUrls.sysadminChildren.enterprises);
    //   })
    //   .catch((error) => {
    //     const errors = !!error?.data ? serializeErrorKeyValues(error?.data) : [t('toast.deletion_failed')];
    //     toast.error(errors?.join(' '));
    //   });
  };

  const onBackButtonClick = () => {
    navigate(routeUrls.sysadminChildren.enterprises);
  };
  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              customWrapperClass: "w-28",
              type: "button",
              variant: "primary-like",
              label: tMain("login_as"),
              onClick: () => handleLogin(enterpriseId),
              disabled: dataSingleEnterprise?.isactive !== 1,
            },
            ...(userCanEdit
              ? [
                  {
                    customWrapperClass: "w-24",
                    type: "button",
                    variant: "secondary",
                    label: tMain("cancel"),
                    onClick: () => onBackButtonClick(),
                  } as FormActionButton,
                ]
              : []),
            {
              customWrapperClass: "w-24",
              type: "button",
              variant: "primary",
              label: userCanEdit ? tMain("update") : tMain("edit"),
              onClick: userCanEdit
                ? handleEditUser
                : () => setUserCanEdit(!userCanEdit),
              disabled:
                isFetchingSingleEnterprise ||
                isLoadingEditAccount ||
                (userCanEdit && !!Object.keys(errors).length) ||
                getUserACLAccessLevel(ACL_NAMES.ACL_SYSADMIN_ACCOUNTS) <
                  ROLE_ACCESS_LEVELS.WRITE,
            },
          ]}
        />
      </>
    );
  }, [
    modalsState,
    isFetchingSingleEnterprise,
    isLoadingEditAccount,
    userCanEdit,
    errors,
    enterpriseId,
  ]);
  const MemoizedFormActionSaveButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              type: "submit",
              variant: "success",
              label: tMain("save"),
              onClick: () => handleEditUser(),
              disabled: isLoadingEditAccount || !!Object.keys(errors).length,
            },
          ]}
        />
      </>
    );
  }, [isLoadingEditAccount, errors]);

  const handleLogin = (accountId?: string) => {
    if (APP_CONFIG.DEBUG.ALL)
      console.log(`Login button clicked for account ID: ${accountId}`);
    const newUrl = routeUrls.sysadminChildren.account_users.replace(
      ":accountId",
      `${accountId}`
    );
    navigate(newUrl);
  };
  return (
    <>
      <HeaderView
        title={t("heading")}
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div className="flex items-center">
          <p className="font-semibold text-accent-blue-dark text-lg leading-6">
            {t("sub_heading")}
          </p>
        </div>
        <div className="lg:grid lg:grid-cols-12 mt-8 py-2 gap-4">
          <div
            className={`hidden xl:block lg:col-span-3 space-y-4${isFetchingEnterprises ? " opacity-40 pointer-events-none" : ""}${isNewEntity?.current ? " xl:hidden" : ""}`}
          >
            <div className="font-bold text-lg leading-6 mt-2 mb-3">
              {t("listing_heading")}
            </div>
            <AppSearchBox
              placeholder={tAdmin("search_placeholder")}
              initialValue={sysadminEnterprisesQueryParams.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                debouncedSetSearchKeyword(e.target.value)
              }
              onClear={() => {
                debouncedSetSearchKeyword("");
              }}
            />
            <div className="max-h-admins overflow-y-auto">
              {dataEnterprises?.results?.map(
                (item: TEnterprise, index: number) => (
                  <AdminListingColumnItem
                    key={index}
                    selected={enterpriseId === item.accountid}
                    onClick={() =>
                      navigate(
                        `${routeUrls.sysadminChildren.enterprises}/${item.accountid}`
                      )
                    }
                    title={enterpriseDisplayName(item)}
                    description={item.description}
                    asideText={
                      item?.isactive ? tMain("active") : tMain("inactive")
                    }
                    bottomText={item.contactname || "-"}
                  />
                )
              )}
            </div>
            {!isFetchingEnterprises && (
              <Pagination
                pageSize={sysadminEnterprisesQueryParams.page_size}
                handlePageSizeChange={(e: TSelectboxOption | null) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      sysadminEnterprises: {
                        ...sysadminEnterprisesQueryParams,
                        page: 1,
                        page_size: parseInt(`${e?.value}`),
                      },
                    })
                  );
                }}
                totalPages={
                  count
                    ? Math.ceil(
                        count / sysadminEnterprisesQueryParams.page_size
                      )
                    : 1
                }
                forcePage={sysadminEnterprisesQueryParams.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      sysadminEnterprises: {
                        ...sysadminEnterprisesQueryParams,
                        page: data?.selected + 1,
                      },
                    })
                  );
                }}
                onlyPageChange={true}
                size={1}
              />
            )}
          </div>
          <div
            className={`${isNewEntity?.current ? "lg:col-span-12" : "col-span-12 xl:col-span-9"}`}
          >
            <div className="flex items-center gap-4">
              {isNewEntity?.current ? (
                <>
                  <p className="font-semibold text-accent-blue-dark text-base leading-6">
                    {tMain("admins.completeCreation")}
                  </p>
                  <div className="flex-grow"></div>
                  {isFetchingSingleEnterprise && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  <div className="w-24">
                    {/* <AdminFormFieldSubmit
                      type="submit"
                      variant="success"
                      label={tMain("save")}
                      onClick={handleEditUser}
                      disabled={isLoadingEditAccount || !!Object.keys(errors).length}
                    /> */}
                    {MemoizedFormActionSaveButton}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-grow"></div>
                  {isFetchingSingleEnterprise && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  {/* ACCOUNTS CANNOT BE DELETED
                  <div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="danger"
                      label={tMain("delete")}
                      onClick={() => { dispatch(setModalsData({ ...modalsState, showDeleteConfirmation: true })) }}
                      disabled={isFetchingSingleEnterprise || isLoadingEditAccount || getUserACLAccessLevel(ACL_NAMES.ACL_SYSADMIN_ACCOUNTS) < ROLE_ACCESS_LEVELS.ALL}
                    />
                  </div> */}
                  {/* <div className="w-24">
                   <AdminFormFieldSubmit
                     label={tMain("login")}
                     variant="primary-like" 
                     onClick={() =>                
                     handleLogin(enterpriseId)
                    }            
                    />
                  </div>
                  {userCanEdit && (<div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="secondary"
                      label={tMain("cancel")}
                      onClick={onBackButtonClick}
                    />
                  </div>)}
                  <div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="primary"
                      label={userCanEdit ? tMain("update") : tMain("edit")}
                      onClick={userCanEdit ? handleEditUser : () => setUserCanEdit(!userCanEdit)}
                      disabled={isFetchingSingleEnterprise || isLoadingEditAccount || (userCanEdit && !!Object.keys(errors).length) || getUserACLAccessLevel(ACL_NAMES.ACL_SYSADMIN_ACCOUNTS) < ROLE_ACCESS_LEVELS.WRITE}
                    />
                  </div> */}
                  {MemoizedFormActionButtons}
                </>
              )}
            </div>
            <div
              className={`rounded-lg mt-2 bg-accent-blue-pale transition ${isFetchingSingleEnterprise || isLoadingEditAccount /* || isLoadingDeleteUser */ ? "opacity-40" : ""}`}
            >
              <form onSubmit={handleSubmit}>
                <Accordian
                  title={t("accord_general_details")}
                  openByDefault={true}
                >
                  <EnterpriseGeneralDetailsForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    formikSetValue={formik.setFieldValue}
                    handleBlur={handleBlur}
                    formikSetTouched={formik.setFieldTouched}
                    userCanEdit={userCanEdit}
                    loadingData={
                      isFetchingSingleEnterprise ||
                      !formikValuesReady ||
                      isFetchingAvailableChildren
                    }
                    eligibleChildOptions={eligibleChildOptions}
                    apiDataFromList={currentEnterpriseList}
                    onParentToggle={handleParentToggle}
                    showParentWarning={showParentWarning}
                    onCancelParentWarning={onCancelParentWarning}
                    onConfirmParentWarning={onConfirmParentWarning}
                  />
                </Accordian>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ModalDeleteConfirmation handleDeleteAdmin={handleDeleteEnterprise} />
    </>
  );
};

export default ScreenSysadminDetailEnterprise;
